import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import { afterReceiving, beforeSending } from '@/services/expense/hooks'

const resource = 'expenses'

type optionsExpenses = {
  _firmId: string
  _id: string
}

const getExpensesByProject = (axios: IFactoryFunctionRequest<optionsExpenses, undefined>) => (projectId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}`,
    afterReceivingHook: afterReceiving,
  })
}

const initExpenses = (axios: IFactoryFunctionRequest<any, any>) => (data) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/initExpenses`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const patchExpense = (axios: IFactoryFunctionRequest<any, any>) => (expenseId: string, data: { expense: {merchant?: string; amount?: number; vat?: number; accountingDate?: string; documents?: any[]; projectId?: string} }) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${expenseId}`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  })
}

const getExpensesList = (axios: IFactoryFunctionRequest<any, any>) => ({ query }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query: query,
    afterReceivingHook: afterReceiving,
  })
}
const createExpense = (axios: IFactoryFunctionRequest<any, any>) => (data: {
  merchant?: string
  amount?: number
  vat?: number
  accountingDate?: string | null
  currency?: string
  mainCurrencyMultiplier?: number
  categoryId?: string | null
  supplierId?: string | null
  isBillable?: boolean
  streamId?: string | null
  projectId?: string | null
}) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
    afterReceivingHook: afterReceiving,
  })
}

const deleteExpenses = (axios: IFactoryFunctionRequest<any, any>) => ({ expenseIds }: { expenseIds?: string[] }) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data: { expenseIds },
  })
}

const exportExpensesToExcel = (axios: IFactoryFunctionRequest<{ base64: string }, any>) => (query: Record<string, any>) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
    query,
    immediate: false,
  })
}

const getEInvoicesList = (axios: IFactoryFunctionRequest<any, any>) => (query: { page?: number; limit?: number; status?: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/eInvoices`,
    query,
  })
}

const syncFromSuperpdp = (axios: IFactoryFunctionRequest<any, any>) => (data?: { invoicingInformationId?: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/eInvoices/sync`,
    data: data || {},
  })
}

const validateEInvoice = (axios: IFactoryFunctionRequest<any, any>) => (id: string, action: 'APPROVE' | 'REJECT') => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/eInvoices/${id}/validate`,
    data: { action },
  })
}

const accountEInvoice = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/eInvoices/${id}/account`,
  })
}

const exportProjectBillableExpensesZip = (axios: IFactoryFunctionRequest<Blob, any>) => (projectId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/project/${projectId}/export-billable-zip`,
    responseType: 'blob',
  })
}

// Expense tool profiles (Jenji) that can be granted by default to a position. Empty for other tools.
const getExpenseProfiles = (axios: IFactoryFunctionRequest<{ _id: string; display: string }[], undefined>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/rights/profiles`,
  })
}

export default (axios: AxiosInstance) => ({
  getExpenseProfiles: getExpenseProfiles(useAxios(axios)),
  getExpensesByProject: getExpensesByProject(useAxios(axios)),
  initExpenses: initExpenses(useAxios(axios)),
  createExpense: createExpense(useAxios(axios)),
  getExpensesList: getExpensesList(useAxios(axios)),
  patchExpense: patchExpense(useAxios(axios)),
  deleteExpenses: deleteExpenses(useAxios(axios)),
  exportExpensesToExcel: exportExpensesToExcel(useAxios(axios)),
  getEInvoicesList: getEInvoicesList(useAxios(axios)),
  syncFromSuperpdp: syncFromSuperpdp(useAxios(axios)),
  validateEInvoice: validateEInvoice(useAxios(axios)),
  accountEInvoice: accountEInvoice(useAxios(axios)),
  exportProjectBillableExpensesZip: exportProjectBillableExpensesZip(useAxios(axios)),
})
